import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Menu, ChevronRight, Shield, Repeat2, LogOut,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useInAppNotifications } from "@/hooks/useInAppNotifications";
import { useUserRoles } from "@/hooks/useUserRoles";
import { detectModuleFromPath, getModuleNav } from "@/lib/modules/mobileNavConfig";

const NEUTRAL_ROUTES = [
  '/profile', '/achievements', '/ranking', '/challenges',
  '/groups', '/pomodoro', '/pomodoro-history', '/plans', '/analytics',
  '/history', '/discipline-goals', '/favorites', '/favorites-study', '/study-stats',
  '/enem/pomodoro', '/oab/pomodoro', '/vestibulares/pomodoro',
];

export function MobileBottomNav() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { notifications } = useInAppNotifications();
  const { isAdmin } = useUserRoles();
  const unreadAchievements = notifications.filter(n => !n.read && n.type === 'achievement').length;

  const isNeutralRoute = NEUTRAL_ROUTES.some(r =>
    location.pathname === r || location.pathname.startsWith(r + '/'));

  const routeModule = detectModuleFromPath(location.pathname);
  const currentModule =
    routeModule
    ?? (isNeutralRoute ? localStorage.getItem('last_active_module') : null)
    ?? 'concursos';

  const { mainItems, moreCategories, colors } = getModuleNav(currentModule);
  const allMoreItems = moreCategories.flatMap(c => c.items);

  const isActive = (path: string) => location.pathname === path;
  const isMoreActive = allMoreItems.some(item => isActive(item.path));

  return (
    <>
      <nav className={cn("fixed bottom-0 left-0 right-0 z-50 md:hidden", colors.border, colors.bg)}>
        <div className="flex items-center justify-around h-16 px-1">
          {mainItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors",
                isActive(item.path) ? colors.active : "text-muted-foreground"
              )}
            >
              <item.icon className="h-5 w-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </button>
          ))}
          <button
            onClick={() => setSheetOpen(true)}
            className={cn(
              "flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors",
              isMoreActive ? colors.active : "text-muted-foreground"
            )}
          >
            <Menu className="h-5 w-5" />
            <span className="text-[10px] font-medium">Mais</span>
          </button>
        </div>
      </nav>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl px-4 pb-8 max-h-[70vh] overflow-y-auto">
          <SheetHeader className="pb-2">
            <SheetTitle>Menu</SheetTitle>
          </SheetHeader>
          <div className="space-y-1">
            {/* Trocar Módulo */}
            <button
              onClick={() => {
                navigate('/select-module');
                setSheetOpen(false);
              }}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-foreground hover:bg-muted transition-colors opacity-0 animate-fade-in"
              style={{ animationDelay: '0ms', animationFillMode: 'forwards' }}
            >
              <Repeat2 className="h-5 w-5 shrink-0 text-primary" />
              <span className="flex-1 text-sm font-medium text-left">Trocar Módulo</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
            <div className="border-b border-border my-2" />
            {(() => {
              let flatIndex = 1;
              return moreCategories.map((category, idx) => {
                const titleIndex = flatIndex++;
                return (
                  <div key={category.title}>
                    {idx > 0 && <div className="border-b border-border my-2" />}
                    <p
                      className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 px-4 pt-2 opacity-0 animate-fade-in"
                      style={{ animationDelay: `${titleIndex * 40}ms`, animationFillMode: 'forwards' }}
                    >
                      {category.title}
                    </p>
                    <div className="flex flex-col">
                      {category.items.map((item) => {
                        const itemIndex = flatIndex++;
                        return (
                          <button
                            key={item.path}
                            onClick={() => {
                              navigate(item.path);
                              setSheetOpen(false);
                            }}
                            className={cn(
                              "flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-colors opacity-0 animate-fade-in",
                              isActive(item.path) ? colors.sheetActive : "text-foreground hover:bg-muted"
                            )}
                            style={{ animationDelay: `${itemIndex * 40}ms`, animationFillMode: 'forwards' }}
                          >
                            <item.icon className="h-5 w-5 shrink-0" />
                            <span className="flex-1 text-sm font-medium text-left">{item.label}</span>
                            {(item.path === '/achievements' || item.path === '/enem/achievements') && unreadAchievements > 0 && (
                              <Badge variant="destructive" className="h-5 min-w-5 px-1 text-[10px]">
                                {unreadAchievements > 9 ? '9+' : unreadAchievements}
                              </Badge>
                            )}
                            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
            {isAdmin && (() => {
              const adminDelay = moreCategories.reduce((acc, c) => acc + 1 + c.items.length, 0);
              return (
                <div>
                  <div className="border-b border-border my-2" />
                  <p
                    className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 px-4 pt-2 opacity-0 animate-fade-in"
                    style={{ animationDelay: `${adminDelay * 40}ms`, animationFillMode: 'forwards' }}
                  >
                    Administração
                  </p>
                  <div className="flex flex-col">
                    <button
                      onClick={() => {
                        navigate('/admin');
                        setSheetOpen(false);
                      }}
                      className={cn(
                        "flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-colors opacity-0 animate-fade-in",
                        location.pathname.startsWith('/admin')
                          ? "bg-primary/10 text-primary border-l-2 border-primary"
                          : "text-foreground hover:bg-muted"
                      )}
                      style={{ animationDelay: `${(adminDelay + 1) * 40}ms`, animationFillMode: 'forwards' }}
                    >
                      <Shield className="h-5 w-5 shrink-0" />
                      <span className="flex-1 text-sm font-medium text-left">Painel Admin</span>
                      <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">Admin</Badge>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </button>
                  </div>
                </div>
              );
            })()}
            <div className="border-b border-border my-2" />
            <button
              onClick={async () => {
                setSheetOpen(false);
                await supabase.auth.signOut();
                navigate('/');
              }}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              <span className="flex-1 text-sm font-medium text-left">Sair</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
